import React, { FC, useState } from "react";
import { HiEllipsisVertical, HiPlus } from "react-icons/hi2";
import { LuTags } from "react-icons/lu";

import { SettingsDropdown } from "../../../../../../../../components/dropdown/SettingsDropdown";
import { LottieLoading } from "../../../../../../../../components/graphics/LottieLoading";
import { Button } from "../../../../../../../../components/newUI/button";
import {
  useDeleteCustomerTagMutation,
  useGetCustomerTagsQuery,
} from "../../../../../../../../slices/ContactTagsSlice";
import { IOrganizationContact } from "../../../../../../../../types/types";
import {
  ProfileWidgetActionButton,
  ProfileWidgetCard,
  ProfileWidgetEmptyState,
} from "../../../../../../components/profile";
import AddTagsModal from "../../../../../../modals/AddTagsModal";
import ErrorPopup from "../../../../../../modals/ErrorPopup";

type Props = {
  organization: IOrganizationContact;
};

export const OrganizationTagsWidget: FC<Props> = ({ organization }) => {
  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenError, setIsOpenError] = useState(false);

  const { data: tags, isFetching } = useGetCustomerTagsQuery({
    customerId: organization.customerId,
  });

  const [deleteCustomerTag] = useDeleteCustomerTagMutation();

  return (
    <>
      {/* Same height and title as the Invoices widget (Roadmap 1880, QA batch 18). */}
      <ProfileWidgetCard
        title={"Tags"}
        grid
        action={
          <ProfileWidgetActionButton
            icon={<HiPlus size={16} />}
            title={"Add tag"}
            onClick={() => setIsOpenAdd(true)}
          />
        }>
        <div>
          {isFetching ? (
            <div className={"p-4"}>
              <LottieLoading />
            </div>
          ) : !tags?.length ? (
            <ProfileWidgetEmptyState
              icon={<LuTags size={28} strokeWidth={1.7} />}
              title={"No tags"}
              description={
                "Tags assigned to this organization will appear here."
              }
            />
          ) : (
            <div
              className={
                "visible-scrollbar max-h-[318px] overflow-y-auto pt-3"
              }>
              {tags?.map((tag) => (
                <div
                  className={
                    "flex min-w-0 items-center justify-between gap-3 border-t border-[#F2F3F6] py-3 pl-5 pr-4 text-[13.5px] font-bold text-[#1F2433]"
                  }
                  key={tag.tags.id}>
                  <span className={"min-w-0 [overflow-wrap:anywhere]"}>
                    {tag.tags.tagName}
                  </span>
                  <SettingsDropdown
                    actionsList={[
                      {
                        name: "Delete",
                        action: () => {
                          deleteCustomerTag({
                            tagIds: tags
                              .filter((item) => item.tags.id !== tag.tags.id)
                              .map((item) => item.tags.id),
                            customerId: organization.customerId,
                          });
                        },
                      },
                    ]}>
                    <Button
                      type={"button"}
                      variant={"outline"}
                      size={"sm"}
                      iconOnly
                      autoWidth
                      leftIcon={<HiEllipsisVertical size={18} />}
                      className={"shrink-0"}
                    />
                  </SettingsDropdown>
                </div>
              ))}
            </div>
          )}
        </div>
      </ProfileWidgetCard>

      <ErrorPopup
        closePopup={() => setIsOpenError(false)}
        isOpen={isOpenError}
      />
      {isOpenAdd && (
        <AddTagsModal
          tags={tags || []}
          onClose={() => setIsOpenAdd(false)}
          customerId={organization.customerId}
        />
      )}
    </>
  );
};
